import {
  Box, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { listOrdersAdmin } from '../../api/orders';
import { listServicesAdmin } from '../../api/services';
import { listEventsAdmin } from '../../api/events';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';
import { CustomerOrderItem, CustomerOrderStatus } from '../../types/customerOrder';
import { ServiceItem } from '../../types/service';
import { EventItem } from '../../types/event';

const STATUSES: CustomerOrderStatus[] = ['Reserved', 'Paid', 'Free', 'Expired', 'Canceled', 'Failed'];

function formatAmount(cents: number) {
  return cents === 0 ? 'Free' : `$${(cents / 100).toFixed(2)}`;
}

function statusColor(status: CustomerOrderStatus): 'success' | 'warning' | 'default' | 'error' {
  if (status === 'Paid' || status === 'Free') return 'success';
  if (status === 'Reserved') return 'warning';
  if (status === 'Expired' || status === 'Canceled' || status === 'Failed') return 'error';
  return 'default';
}

export function OrdersAdminPage() {
  const { request: listOrdersRequest } = useApiCallWithAuth(listOrdersAdmin);
  const { request: listServicesRequest } = useApiCallWithAuth(listServicesAdmin);
  const { request: listEventsRequest } = useApiCallWithAuth(listEventsAdmin);

  const [orders, setOrders] = useState<CustomerOrderItem[]>([]);
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState('All');
  const [itemFilter, setItemFilter] = useState('All');

  useEffect(() => {
    Promise.all([listServicesRequest(), listEventsRequest()])
      .then(([s, e]) => { setServices(s); setEvents(e); })
      .catch(() => setError('Failed to load services/events'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function loadOrders() {
    setLoading(true);
    const filters: { status?: string; serviceGuid?: string; eventGuid?: string } = {};
    if (statusFilter !== 'All') filters.status = statusFilter;
    if (itemFilter !== 'All') {
      const [kind, guid] = itemFilter.split(':');
      if (kind === 'service') filters.serviceGuid = guid;
      if (kind === 'event') filters.eventGuid = guid;
    }
    listOrdersRequest(filters)
      .then(setOrders)
      .catch(() => setError('Failed to load orders'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadOrders(); }, [statusFilter, itemFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  function itemName(order: CustomerOrderItem): string {
    if (order.serviceGuid) return services.find(s => s.serviceGuid === order.serviceGuid)?.name ?? 'Service';
    return events.find(e => e.eventGuid === order.eventGuid)?.name ?? 'Event';
  }

  return (
    <Box sx={{ pt: 3 }}>
      <Typography variant='h5' fontWeight={700} sx={{ mb: 2 }}>Orders &amp; Registrations</Typography>

      <Stack direction='row' spacing={2} sx={{ mb: 2 }}>
        <FormControl size='small' sx={{ minWidth: 200 }}>
          <InputLabel id='item-filter-label'>Service / Event</InputLabel>
          <Select
            labelId='item-filter-label'
            value={itemFilter}
            label='Service / Event'
            onChange={e => setItemFilter(e.target.value)}
          >
            <MenuItem value='All'>All</MenuItem>
            {services.map(s => (
              <MenuItem key={s.serviceGuid} value={`service:${s.serviceGuid}`}>{s.name}</MenuItem>
            ))}
            {events.map(e => (
              <MenuItem key={e.eventGuid} value={`event:${e.eventGuid}`}>{e.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size='small' sx={{ minWidth: 160 }}>
          <InputLabel id='status-filter-label'>Status</InputLabel>
          <Select
            labelId='status-filter-label'
            value={statusFilter}
            label='Status'
            onChange={e => setStatusFilter(e.target.value)}
          >
            <MenuItem value='All'>All</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      {loading && <CircularProgress size={24} />}
      {error && <Typography color='error' sx={{ mb: 2 }}>{error}</Typography>}

      {!loading && !error && orders.length === 0 && (
        <Typography color='text.secondary'>No orders yet.</Typography>
      )}

      {!loading && orders.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Service / Event</strong></TableCell>
              <TableCell><strong>Amount</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Confirmation #</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map(order => (
              <TableRow key={order.customerOrderGuid} hover>
                <TableCell><Typography variant='body2'>{order.email}</Typography></TableCell>
                <TableCell><Typography variant='body2'>{order.name ?? '—'}</Typography></TableCell>
                <TableCell><Typography variant='body2'>{itemName(order)}</Typography></TableCell>
                <TableCell><Typography variant='body2'>{formatAmount(order.amountCents)}</Typography></TableCell>
                <TableCell>
                  <Chip label={order.status} size='small' color={statusColor(order.status)} />
                </TableCell>
                <TableCell><Typography variant='body2'>{order.confirmationNumber}</Typography></TableCell>
                <TableCell>
                  <Typography variant='body2' color='text.secondary'>
                    {new Date(order.dateCreated).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
